import { ProjectType } from "@/types";
import { create } from "zustand";
// import { createJSONStorage, persist } from 'zustand/middleware';

// Store pour gérer le pourcentage
const usePercentageStore = create<{
  enablePreviewLine?: boolean;
  setEnablePreviewLine: (val: boolean) => void;
  percentage: number;
  setPercentage: (value: number) => void;
  resetPercentage: () => void;
}>((set) => ({
  percentage: 0,
  enablePreviewLine: true,
  setEnablePreviewLine: (value) => set({ enablePreviewLine: value }),
  setPercentage: (value) => set({ percentage: value }),
  resetPercentage: () => set({ percentage: 0 }),
}));

type ProjectState = {
  isProjectApiReady: boolean;
  project: ProjectType | null;
  projectExportContextById: Record<number, any>;
  setIsProjectApiReady: (value: boolean) => void;
  setProject: (
    update: ProjectType | ((prev: ProjectType | null) => ProjectType),
  ) => void;
  setProjectExportContext: (projectId: number, context: any) => void;
};

const useProjectStore = create<ProjectState>((set) => ({
  isProjectApiReady: true,
  project: null,
  projectExportContextById: {},
  setIsProjectApiReady: (value) => set({ isProjectApiReady: value }),
  setProject: (update) =>
    set((state) => ({
      project: typeof update === "function" ? update(state.project) : update,
    })),
  setProjectExportContext: (projectId, context) =>
    set((state) => ({
      projectExportContextById: {
        ...state.projectExportContextById,
        [projectId]: context,
      },
    })),
}));

const useFundingByProject = create((set) => ({
  data: null,
  setData: (data) => set({ data }),
}));

const useFundingByFinancer = create((set) => ({
  data: null,
  setData: (data) => set({ data }),
}));

// Store pour gérer l'état des modals
const useModal = create<{
  modals: { [key: string]: boolean };
  setOpenModal: (modal: {
    name: string;
    value: boolean;
    programId?: number;
    programName?: string;
  }) => void;
  programId?: number;
  programName?: string;
}>((set) => ({
  modals: {},
  setOpenModal: ({ name, value, programId, programName }) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [name]: value,
      },
      programId,
      programName,
    })),
}));

// Clé utilisée pour stocker les données dans localStorage
const STORAGE_KEY = "previewPerformance";

const usePreviewPerformance = create<{
  previews: { [key: string]: string };
  setPreviews: (preview: { name: string; value: string }) => void;
}>((set) => ({
  // Initialisation des données depuis localStorage
  previews: JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"),

  // Mise à jour de l'état et de localStorage
  setPreviews: ({ name, value }) => {
    set((state) => {
      const updatedPreviews = {
        ...state.previews,
        [name]: value,
      };

      // Synchronisation avec localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedPreviews));

      return { previews: updatedPreviews };
    });
  },
}));

const LEFTBAR_STORAGE_KEY = "dashboard:leftbar:is-open";

const readLeftbarInitialState = () => {
  if (typeof window === "undefined") return true;

  try {
    return window.localStorage.getItem(LEFTBAR_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
};

const useLeftbar = create<{
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}>((set) => ({
  isOpen: readLeftbarInitialState(),
  setIsOpen: (value) => {
    set(() => {
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(
            LEFTBAR_STORAGE_KEY,
            value ? "1" : "0",
          );
        }
      } catch {
        // Le cache local reste une optimisation.
      }

      return { isOpen: value };
    });
  },
}));

type TabState = {
  tab: "synthetic" | "descriptif" | "suivi-perf" | "reporting" | "document";
  setTab: (
    tab: "synthetic" | "descriptif" | "suivi-perf" | "reporting " | "document",
  ) => void;
};

const useTabDashboard = create<TabState>((set) => ({
  tab: "descriptif",
  setTab: (tab) => set({ tab }),
}));

const useCustomTheme = create<{
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
}>((set) => ({
  // Initialisation des données depuis localStorage
  isVisible: false,
  // Mise à jour de l'état et de localStorage
  setIsVisible: (value) => {
    set(() => {
      return { isVisible: value };
    });
  },
}));

interface LogoStore {
  logoUrl: string;
  setLogoUrl: (url: string) => void;
}

const useLogoStore = create<LogoStore>((set) => ({
  logoUrl: "", // Valeur par défaut
  setLogoUrl: (url: string) => set({ logoUrl: url }),
}));

interface ErrorLog {
  message?: string;
  name?: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  userAgent?: string;
  time?: string;
}

interface FeedbackStore {
  // UI
  visibleFeedback: boolean;
  setVisibleFeedback: (visibleFeedback: boolean) => void;

  // 💥 Logs techniques
  errorLog: ErrorLog | null;
  setErrorLog: (log: ErrorLog) => void;
  clearErrorLog: () => void;
}
const useFeedback = create<FeedbackStore>((set) => ({
  visibleFeedback: false,
  setVisibleFeedback: (visible) => set({ visibleFeedback: visible }),
  errorLog: null,
  setErrorLog: (log) => set({ errorLog: log }),
  clearErrorLog: () => set({ errorLog: null }),
}));
export {
  useCustomTheme,
  useFeedback,
  useFundingByFinancer,
  useFundingByProject,
  useLeftbar,
  useLogoStore,
  useModal,
  usePercentageStore,
  usePreviewPerformance,
  useProjectStore,
  useTabDashboard,
};
